import { PaginationKeys, usePaginationState } from '@lune-fe/lune-components-lib'
import { ButtonNew, formatNumbers, SidePanel, TableNew } from '@lune-fe/lune-ui-lib'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'
import Box from '@mui/material/Box'
import { FC, ReactNode, RefObject, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import useToken from 'hooks/useToken'
import {
    EmissionCalculationDetails,
    EmissionConditionalTooltip,
    EstimateType,
    EstimateTypeSlug,
} from 'views/CalculateEmissions/index'
import { useEstimate } from 'views/CalculateEmissions/Tables/Shared/hooks/useEstimate'

import DownloadPdfButton from '../../Shared/DownloadPdfButton'

interface EmissionCalculationRowProps {
    id: string
    slug: string | undefined
    emissionType: string
    estimateType: EstimateType
    children: ReactNode
    contextMenu: ReactNode
    currentPage: number
    pageHistoryRef: RefObject<(string | undefined)[]>
    queryParams: string | undefined
    /**
     * If provided, shows a checkbox as the first cell
     */
    showCheckbox?: boolean
    /**
     * Whether the row is selected (only applies when showCheckbox is true)
     */
    isSelected?: boolean
    /**
     * Callback when checkbox state changes (only applies when showCheckbox is true)
     */
    onCheckboxChange?: (checked: boolean) => void
}

const EmissionCalculationRow: FC<EmissionCalculationRowProps> = ({
    id,
    slug,
    emissionType,
    estimateType,
    children,
    contextMenu,
    currentPage,
    queryParams,
    pageHistoryRef,
    showCheckbox = false,
    isSelected = false,
    onCheckboxChange,
}) => {
    const [sidePanelOpen, setSidePanelOpen] = useState(false)
    const navigate = useNavigate()
    const { savePaginationState } = usePaginationState()
    const { token } = useToken()

    // Fetch estimate data when panel opens
    const { estimate, resolvedLegs, loading } = useEstimate(sidePanelOpen ? id : null, estimateType)

    const handleRowClick = (event: React.MouseEvent) => {
        if (!slug) {
            return
        }
        if (event.metaKey || event.ctrlKey) {
            // Open in new tab
            window.open(
                `/calculate-emissions/${EstimateTypeSlug(estimateType)}/${id}/results`,
                '_blank',
            )
        } else {
            setSidePanelOpen(true)
        }
    }

    return (
        <>
            <TableNew.TableRow>
                <TableNew.TableCell
                    colSpan={12}
                    colSize={12}
                    sx={{ pl: `0 !important`, pr: `0 !important` }}
                >
                    <EmissionConditionalTooltip slug={slug} emissionType={emissionType}>
                        <TableNew.TableContainer>
                            <TableNew
                                sx={{
                                    tableLayout: 'fixed',
                                }}
                                bodyCells={
                                    <TableNew.TableRow
                                        withSelectCheckbox={showCheckbox}
                                        data-testid={id}
                                        selected={isSelected}
                                        hover
                                        sx={{
                                            '&:hover': {
                                                '.MuiButtonBase-root': {
                                                    opacity: 1,
                                                },
                                                ...(slug
                                                    ? {}
                                                    : {
                                                          cursor: 'not-allowed',
                                                      }),
                                            },
                                        }}
                                        {...(slug
                                            ? {
                                                  onClick: (e) => handleRowClick(e),
                                              }
                                            : {})}
                                    >
                                        {showCheckbox && (
                                            <TableNew.TableCheckboxCell
                                                checked={isSelected}
                                                onCheckedChange={onCheckboxChange || (() => {})}
                                                showOnHover={true}
                                            />
                                        )}
                                        {children}
                                        <TableNew.TableCell
                                            colSize={1}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                e.preventDefault()
                                            }}
                                            alignEnd
                                            sx={{
                                                '.MuiButton-root': {
                                                    opacity: 0,
                                                },
                                                '&:hover': {
                                                    '.MuiButton-root': {
                                                        opacity: 1,
                                                    },
                                                },
                                            }}
                                        >
                                            {contextMenu}
                                        </TableNew.TableCell>
                                    </TableNew.TableRow>
                                }
                            />
                        </TableNew.TableContainer>
                    </EmissionConditionalTooltip>
                </TableNew.TableCell>
            </TableNew.TableRow>
            <SidePanel
                open={sidePanelOpen}
                onClose={() => setSidePanelOpen(false)}
                onExpandClick={() => {
                    savePaginationState(
                        PaginationKeys.EMISSION_CALCULATION_LOGISTICS,
                        currentPage,
                        pageHistoryRef.current || [undefined],
                    )
                    navigate(
                        `/calculate-emissions/${EstimateTypeSlug(
                            estimateType,
                        )}/${id}/results${queryParams ? `?${queryParams}` : ''}`,
                    )
                }}
                headerContent={
                    estimate &&
                    'mass' in estimate &&
                    token && (
                        <Box
                            sx={{
                                gap: 2,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <DownloadPdfButton
                                id={estimate.id}
                                token={token}
                                isMultiLeg={[
                                    'multi_leg_shipping',
                                    'multi_leg_shipping_by_fuel',
                                ].includes(estimateType)}
                            />
                            <ButtonNew
                                variant="primary"
                                style="fill"
                                size="sm"
                                leadingIcon={<CloudDownloadOutlinedIcon />}
                                onClick={() =>
                                    navigate(
                                        `/order?amount=${formatNumbers(estimate.mass.amount, 2)}`,
                                    )
                                }
                            >
                                Offset Now
                            </ButtonNew>
                        </Box>
                    )
                }
            >
                <EmissionCalculationDetails
                    estimate={estimate}
                    resolvedLegs={resolvedLegs}
                    loading={loading}
                    estimateType={estimateType}
                />
            </SidePanel>
        </>
    )
}

export default EmissionCalculationRow
